import {
    useMemo,
    useCallback,
} from 'react'
import isEmpty from 'lodash/isEmpty'
import {
    useFetcher,
    useLoaderData,
    useLocation,
    useOutletContext,
} from 'react-router-dom'

import {
    ActionComponentFunction,
} from 'app/shared-components/SkyNetDomain/SkyNetDomain.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest'

import {
    SkyNetLoaderResponse,
    SkyNetObjectFormOutletContext,
} from '../../SkyNetObjectRoutes.types'

export default function SkyNetObjectFormWrapper<Data>({
    component: Component,
    tabRequestKey,
}: {
    component: ((props: {
        data?: Data,
        onSuccess?: () => void,
        onCancel?: () => void,
    }) => JSX.Element) | ActionComponentFunction,
    tabRequestKey: RequestKeys,
}) {
    const {
        data: preLoadedData,
        invalidate,
    }: SkyNetLoaderResponse<Data> = useLoaderData() || {}
    const {
        backPath,
        refreshTable,
    } = useOutletContext<SkyNetObjectFormOutletContext>()
    const location = useLocation()
    const fetcher = useFetcher()

    const fullOnSuccess = useCallback(() => {
        invalidate() // invalidate query for router.loader
        refreshTable(tabRequestKey) // do update table
        fetcher.load(location.pathname) // get new data from router.loader
    }, [
        invalidate,
        tabRequestKey,
        refreshTable,
        location,
        fetcher,
    ])

    const isLoading = [
        'loading',
        'submitting',
    ].includes(fetcher.state)

    const props = useMemo(() => {
        return (fetcher.data?.data || !isEmpty(preLoadedData)) ? {
            data: fetcher.data?.data || preLoadedData,
            onCancel: invalidate,
            onSuccess: fullOnSuccess,
            onClose: invalidate,
            backPath,
        } : {}
    }, [
        preLoadedData,
        invalidate,
        backPath,
        fullOnSuccess,
        fetcher.data,
    ])

    return (
        <div data-testid="snd-form-wrapper">
            {!isLoading && (
                <Component
                    {...props} // eslint-disable-line react/jsx-props-no-spreading
                />
            )}
        </div>
    )
}
